'use client';

import type {
  CurrencyFilterMeta,
  DateFilterMeta,
  DateTimeFilterMeta,
  FilterType,
  NumberFilterMeta,
  TextFilterMeta,
  TimeFilterMeta,
} from '../../types/filter-meta';
import type { FilterOption } from '../../types/filter-option';
import type {
  CheckboxFilterValue,
  CurrencyFilterValue,
  DateFilterValue,
  DateTimeFilterValue,
  FilterValue,
  NumberFilterValue,
  SelectFilterValue,
  TextFilterValue,
  TimeFilterValue,
} from '../../types/filter-values';
import type { FilterRendererSlotProps } from '../../types/toolbar-slot-props';
import { CheckboxFilter } from './checkbox-filter';
import { CurrencyFilter } from './currency-filter';
import { DateFilter } from './date-filter';
import { DateTimeFilter } from './datetime-filter';
import { NumberFilter } from './number-filter';
import { SelectFilter } from './select-filter';
import { TagFilter } from './tag-filter';
import { TextFilter } from './text-filter';
import { TimeFilter } from './time-filter';

export type FilterRendererProps = FilterRendererSlotProps;

function renderSelectFilter(
  options: FilterOption[],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <SelectFilter
      options={options}
      value={value as SelectFilterValue | undefined}
      onChange={onChange as (value: SelectFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderTagFilter(
  options: FilterOption[],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <TagFilter
      options={options}
      value={value as SelectFilterValue | undefined}
      onChange={onChange as (value: SelectFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderTextFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <TextFilter
      filterMeta={filterType === 'text' ? (filterMeta as TextFilterMeta) : undefined}
      value={value as TextFilterValue | undefined}
      onChange={onChange as (value: TextFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderNumberFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  range: { min: number; max: number } | undefined,
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <NumberFilter
      filterMeta={filterType === 'number' ? (filterMeta as NumberFilterMeta) : undefined}
      range={range}
      value={value as NumberFilterValue | undefined}
      onChange={onChange as (value: NumberFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderCurrencyFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  range: { min: number; max: number } | undefined,
  currencyCode: string | undefined,
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <CurrencyFilter
      filterMeta={filterType === 'currency' ? (filterMeta as CurrencyFilterMeta) : undefined}
      range={range}
      currencyCode={currencyCode}
      value={value as CurrencyFilterValue | undefined}
      onChange={onChange as (value: CurrencyFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderDateFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <DateFilter
      filterMeta={filterType === 'date' ? (filterMeta as DateFilterMeta) : undefined}
      value={value as DateFilterValue | undefined}
      onChange={onChange as (value: DateFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderTimeFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <TimeFilter
      filterMeta={filterType === 'time' ? (filterMeta as TimeFilterMeta) : undefined}
      value={value as TimeFilterValue | undefined}
      onChange={onChange as (value: TimeFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderDateTimeFilter(
  filterType: FilterType,
  filterMeta: FilterRendererProps['filterMeta'],
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <DateTimeFilter
      filterMeta={filterType === 'datetime' ? (filterMeta as DateTimeFilterMeta) : undefined}
      value={value as DateTimeFilterValue | undefined}
      onChange={onChange as (value: DateTimeFilterValue | undefined) => void}
      className={className}
    />
  );
}

function renderCheckboxFilter(
  value: FilterValue | undefined,
  onChange: (value: FilterValue | undefined) => void,
  className?: string,
) {
  return (
    <CheckboxFilter
      value={value as CheckboxFilterValue | undefined}
      onChange={onChange as (value: CheckboxFilterValue | undefined) => void}
      className={className}
    />
  );
}

export function FilterRenderer({
  filterType,
  filterMeta,
  options,
  range,
  value,
  onChange,
  className,
}: FilterRendererProps) {
  switch (filterType) {
    case 'select':
      return renderSelectFilter(options, value, onChange, className);
    case 'tag':
      return renderTagFilter(options, value, onChange, className);
    case 'text':
      return renderTextFilter(filterType, filterMeta, value, onChange, className);
    case 'number':
      return renderNumberFilter(filterType, filterMeta, range, value, onChange, className);
    case 'currency':
      return renderCurrencyFilter(
        filterType,
        filterMeta,
        range,
        (filterMeta as CurrencyFilterMeta).currencyCode,
        value,
        onChange,
        className,
      );
    case 'date':
      return renderDateFilter(filterType, filterMeta, value, onChange, className);
    case 'time':
      return renderTimeFilter(filterType, filterMeta, value, onChange, className);
    case 'datetime':
      return renderDateTimeFilter(filterType, filterMeta, value, onChange, className);
    case 'checkbox':
      return renderCheckboxFilter(value, onChange, className);
    default:
      return null;
  }
}
